import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TrendingUp, TrendingDown, Pencil, Plus, Trash2, ArrowUp, ArrowDown, Maximize2, Minimize2 } from "lucide-react";
import HotelWeeklyBoard from "@/components/HotelWeeklyBoard";
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, Legend,
  CartesianGrid, BarChart, LabelList,
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => (n === null || n === undefined ? "—" : `$${Number(n).toLocaleString("en-AU", { maximumFractionDigits: 0 })}`);
const fmt2 = (n) => (n === null || n === undefined ? "—" : `$${Number(n).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const tooltipStyle = { borderRadius: 12, border: "1px solid #E2E8F0", boxShadow: "0 8px 24px rgba(15,59,92,0.12)", fontSize: 12 };
const COST_FIELDS = [
  ["labor", "Labor / wages"],
  ["utilities", "Utilities (power, gas, water)"],
  ["supplies", "Laundry supplies (detergent etc.)"],
  ["rent", "Rent"],
  ["vehicle", "Vehicle / fuel"],
  ["maintenance", "Maintenance"],
];

export default function AnalyticsTab() {
  const [summary, setSummary] = useState(null);
  const [clients, setClients] = useState(null);
  const [turnover, setTurnover] = useState(null);
  const [editMonth, setEditMonth] = useState(null);
  const [replCost, setReplCost] = useState("");
  const [plExpanded, setPlExpanded] = useState(false);
  // client revenue chart
  const [clientMode, setClientMode] = useState("week"); // week | total
  const [clientDesc, setClientDesc] = useState(true);
  const [chartExpanded, setChartExpanded] = useState(false);
  // clients table
  const [tableSort, setTableSort] = useState({ key: "revenue_ex_gst", desc: true });
  const [tableExpanded, setTableExpanded] = useState(false);
  // turnover
  const [turnoverMonth, setTurnoverMonth] = useState(null);
  const [turnoverExpanded, setTurnoverExpanded] = useState(false);

  const load = async (monthsArg) => {
    const m = monthsArg ?? (plExpanded ? 36 : 12);
    try {
      const [s, c, t] = await Promise.all([
        axios.get(`${API}/analytics/summary?months=${m}`),
        axios.get(`${API}/analytics/clients?months=12`),
        axios.get(`${API}/analytics/turnover`),
      ]);
      setSummary(s.data);
      setClients(c.data);
      setTurnover(t.data);
      setReplCost(String(s.data.replacement_cost || ""));
    } catch (e) {
      toast.error("Failed to load analytics");
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const months = summary?.months || [];
  const chartData = useMemo(() => months.slice(-12).map((m) => ({
    month: m.month.slice(2),
    Revenue: m.revenue_total,
    Costs: m.cost_total,
    Profit: m.profit,
  })), [months]);
  const latest = months[months.length - 1];

  useEffect(() => {
    if (!turnoverMonth && months.length) setTurnoverMonth(months[months.length - 1].month);
  }, [months, turnoverMonth]);

  const saveReplCost = async () => {
    try {
      await axios.put(`${API}/analytics/settings`, { replacement_cost: Number(replCost) || 0 });
      toast.success("Replacement cost saved");
      load();
    } catch (e) { toast.error("Save failed"); }
  };

  const togglePlExpand = () => {
    const next = !plExpanded;
    setPlExpanded(next);
    load(next ? 36 : 12);
  };

  // ---- client chart data ----
  const metricKey = clientMode === "week4" ? "revenue_per_week_4w" : clientMode === "week" ? "revenue_per_week" : "revenue_ex_gst";
  const clientChartData = useMemo(() => {
    const list = [...(clients?.clients || [])].sort((a, b) =>
      clientDesc ? b[metricKey] - a[metricKey] : a[metricKey] - b[metricKey]);
    return chartExpanded ? list : list.slice(0, 8);
  }, [clients, metricKey, clientDesc, chartExpanded]);

  // ---- clients table data ----
  const tableData = useMemo(() => {
    const list = [...(clients?.clients || [])].sort((a, b) => {
      const av = a[tableSort.key] ?? -Infinity, bv = b[tableSort.key] ?? -Infinity;
      if (typeof av === "string") return tableSort.desc ? bv.localeCompare(av) : av.localeCompare(bv);
      return tableSort.desc ? bv - av : av - bv;
    });
    return tableExpanded ? list : list.slice(0, 10);
  }, [clients, tableSort, tableExpanded]);

  const sortBy = (key) => setTableSort((s) => ({ key, desc: s.key === key ? !s.desc : true }));
  const Th = ({ label, k, right = true }) => (
    <th
      className={`py-2 pr-2 cursor-pointer select-none hover:text-slate-900 whitespace-nowrap ${right ? "text-right" : "text-left"}`}
      onClick={() => sortBy(k)}
      data-testid={`clients-sort-${k}`}
    >
      {label}
      {tableSort.key === k && (tableSort.desc
        ? <ArrowDown className="inline h-3 w-3 ml-0.5" />
        : <ArrowUp className="inline h-3 w-3 ml-0.5" />)}
    </th>
  );

  const selTurnover = months.find((m) => m.month === turnoverMonth);
  const totalItems = turnover?.total_items || 0;

  return (
    <div className="space-y-6" data-testid="analytics-tab">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-display text-2xl font-bold text-brand-navy">Analytics</h2>
          <p className="text-sm font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-0.5 inline-block mt-1">
            All amounts are EX-GST (AUD)
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Replacement cost / piece (ex-GST)</span>
          <Input data-testid="analytics-repl-cost" className="w-24 h-9" type="number" value={replCost} onChange={(e) => setReplCost(e.target.value)} />
          <Button data-testid="analytics-repl-cost-save" size="sm" variant="outline" onClick={saveReplCost}>Save</Button>
        </div>
      </div>

      {latest && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Kpi label={`Revenue ${latest.month}`} value={fmt(latest.revenue_total)} tone="blue" testid="analytics-kpi-revenue" />
          <Kpi label={`Costs ${latest.month}`} value={fmt(latest.cost_total)} tone="amber" testid="analytics-kpi-costs" />
          <Kpi label={`Profit ${latest.month}`} value={fmt(latest.profit)} tone={latest.profit >= 0 ? "green" : "red"} icon={latest.profit >= 0 ? TrendingUp : TrendingDown} testid="analytics-kpi-profit" />
          <Kpi label="Cost / piece dispatched" value={latest.cost_per_item != null ? `$${latest.cost_per_item}` : "—"} tone="neutral" testid="analytics-kpi-cpp" />
        </div>
      )}

      {/* P&L chart */}
      <Card>
        <CardHeader><CardTitle className="text-base">Revenue · Costs · Profit (12 months, ex-GST)</CardTitle></CardHeader>
        <CardContent className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartData}>
              <defs>
                <linearGradient id="gradRevenue" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#0F3B5C" stopOpacity={0.95} />
                  <stop offset="100%" stopColor="#0F3B5C" stopOpacity={0.55} />
                </linearGradient>
                <linearGradient id="gradCosts" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#F59E0B" stopOpacity={0.9} />
                  <stop offset="100%" stopColor="#F59E0B" stopOpacity={0.45} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="2 4" stroke="#E2E8F0" vertical={false} />
              <XAxis dataKey="month" fontSize={11} tick={{ fill: "#64748B" }} axisLine={{ stroke: "#E2E8F0" }} tickLine={false} />
              <YAxis fontSize={11} tick={{ fill: "#64748B" }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(v) => fmt2(v)} contentStyle={tooltipStyle} cursor={{ fill: "rgba(2,132,199,0.05)" }} />
              <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="Revenue" fill="url(#gradRevenue)" radius={[6, 6, 0, 0]} maxBarSize={34} />
              <Bar dataKey="Costs" fill="url(#gradCosts)" radius={[6, 6, 0, 0]} maxBarSize={34} />
              <Line dataKey="Profit" stroke="#059669" strokeWidth={2.5} dot={{ r: 3, fill: "#059669", stroke: "#fff", strokeWidth: 1.5 }} activeDot={{ r: 5 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Monthly table */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base">Monthly P&L (ex-GST) — click ✎ to enter costs</CardTitle>
          <Button data-testid="pl-expand-btn" size="sm" variant="outline" onClick={togglePlExpand}>
            {plExpanded ? <><Minimize2 className="h-3.5 w-3.5 mr-1" /> Last 12 months</> : <><Maximize2 className="h-3.5 w-3.5 mr-1" /> Expand all</>}
          </Button>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="analytics-months-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground border-b">
                <th className="py-2 pr-3">Month</th>
                <th className="py-2 pr-3 text-right">Revenue</th>
                <th className="py-2 pr-3 text-right">Costs</th>
                <th className="py-2 pr-3 text-right">Profit</th>
                <th className="py-2 pr-3 text-right whitespace-nowrap">Washed pcs<RfidTag /></th>
                <th className="py-2 pr-3 text-right">Cost/pc</th>
                <th className="py-2 pr-3 text-right">Dispatched</th>
                <th className="py-2 pr-3 text-right whitespace-nowrap">Written off<RfidTag /></th>
                <th className="py-2 pr-3 text-right">Loss est.</th>
                <th className="py-2" />
              </tr>
            </thead>
            <tbody>
              {[...months].reverse().map((m) => (
                <tr key={m.month} className="border-b last:border-0 hover:bg-neutral-50" data-testid={`analytics-row-${m.month}`}>
                  <td className="py-2 pr-3 font-mono font-semibold">{m.month}</td>
                  <td className="py-2 pr-3 text-right font-mono">
                    {fmt(m.revenue_total)}
                    {m.revenue_adjustment !== 0 && <span className="text-[10px] text-sky-600 block">adj {m.revenue_adjustment > 0 ? "+" : ""}{fmt(m.revenue_adjustment)}</span>}
                  </td>
                  <td className="py-2 pr-3 text-right font-mono">{m.has_costs ? fmt(m.cost_total) : <span className="text-neutral-300">not entered</span>}</td>
                  <td className={`py-2 pr-3 text-right font-mono font-bold ${m.profit >= 0 ? "text-emerald-700" : "text-red-600"}`}>{m.has_costs ? fmt(m.profit) : "—"}</td>
                  <td className="py-2 pr-3 text-right font-mono">{m.received_items || "—"}</td>
                  <td className="py-2 pr-3 text-right font-mono">{m.cost_per_item != null ? `$${m.cost_per_item}` : "—"}</td>
                  <td className="py-2 pr-3 text-right font-mono">{m.dispatched_items || "—"}</td>
                  <td className="py-2 pr-3 text-right font-mono">{m.deleted_items ? <span className="text-red-600 font-bold">{m.deleted_items}</span> : "—"}</td>
                  <td className="py-2 pr-3 text-right font-mono text-red-600">{m.loss_estimate ? fmt(m.loss_estimate) : "—"}</td>
                  <td className="py-2 text-right">
                    <Button data-testid={`analytics-edit-${m.month}`} size="sm" variant="ghost" onClick={() => setEditMonth(m)}><Pencil className="h-4 w-4" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Client revenue chart */}
      <Card>
        <CardHeader className="space-y-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden">
                <button
                  data-testid="client-mode-week4"
                  onClick={() => setClientMode("week4")}
                  className={`px-3 py-1.5 text-xs font-semibold transition-colors ${clientMode === "week4" ? "bg-brand-navy text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
                >
                  Avg/wk · 4w
                </button>
                <button
                  data-testid="client-mode-week"
                  onClick={() => setClientMode("week")}
                  className={`px-3 py-1.5 text-xs font-semibold transition-colors border-l border-slate-200 ${clientMode === "week" ? "bg-brand-navy text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
                >
                  Avg/wk · 12m
                </button>
                <button
                  data-testid="client-mode-total"
                  onClick={() => setClientMode("total")}
                  className={`px-3 py-1.5 text-xs font-semibold transition-colors border-l border-slate-200 ${clientMode === "total" ? "bg-brand-navy text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
                >
                  Total · 12m
                </button>
              </div>
              <Button data-testid="client-sort-btn" size="sm" variant="outline" className="h-8" onClick={() => setClientDesc((d) => !d)}>
                {clientDesc ? <ArrowDown className="h-3.5 w-3.5" /> : <ArrowUp className="h-3.5 w-3.5" />}
              </Button>
            </div>
            <div className="flex items-center gap-2">
              <CardTitle className="text-base hidden sm:block">Client revenue (ex-GST)</CardTitle>
              <Button data-testid="client-chart-expand" size="sm" variant="outline" className="h-8" onClick={() => setChartExpanded((e) => !e)}>
                {chartExpanded ? <><Minimize2 className="h-3.5 w-3.5 mr-1" /> Top 8</> : <><Maximize2 className="h-3.5 w-3.5 mr-1" /> All clients</>}
              </Button>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            {clientMode === "week4"
              ? "Average weekly revenue over the last 4 weeks"
              : clientMode === "week"
                ? "Average weekly revenue since each client's first order (fair for newer clients)"
                : "Total revenue over the last 12 months"}
          </p>
          {clients?.totals && (
            <div className="flex flex-wrap gap-2 pt-1" data-testid="clients-totals">
              <span className="text-xs font-semibold bg-slate-100 border border-slate-200 rounded-full px-3 py-1">
                All hotels · 4w avg/wk: <span className="font-mono text-brand-navy">{fmt(clients.totals.per_week_4w)}</span>
              </span>
              <span className="text-xs font-semibold bg-slate-100 border border-slate-200 rounded-full px-3 py-1">
                12m avg/wk: <span className="font-mono text-brand-navy">{fmt(clients.totals.per_week_12m)}</span>
              </span>
              <span className="text-xs font-semibold bg-slate-100 border border-slate-200 rounded-full px-3 py-1">
                12m total: <span className="font-mono text-brand-navy">{fmt(clients.totals.total_12m)}</span>
              </span>
            </div>
          )}
        </CardHeader>
        <CardContent style={{ height: Math.max(260, clientChartData.length * 36 + 40) }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={clientChartData} layout="vertical" margin={{ left: 10, right: 60 }}>
              <defs>
                <linearGradient id="gradClient" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="#0175cc" />
                  <stop offset="100%" stopColor="#0175cc" />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="2 4" stroke="#E2E8F0" horizontal={false} />
              <XAxis type="number" fontSize={11} tick={{ fill: "#64748B" }} axisLine={false} tickLine={false} tickFormatter={(v) => (v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`)} />
              <YAxis type="category" dataKey="hotel_name" width={190} fontSize={12} tick={{ fill: "#334155", fontWeight: 600 }} axisLine={false} tickLine={false} />
              <Tooltip formatter={(v) => fmt2(v)} contentStyle={tooltipStyle} cursor={{ fill: "rgba(1,117,204,0.06)" }} />
              <Bar dataKey={metricKey} name={clientMode === "week4" ? "Avg $/wk (4w)" : clientMode === "week" ? "Avg $/week" : "Revenue"} fill="#0175cc" radius={[0, 8, 8, 0]} maxBarSize={22} isAnimationActive={false}>
                <LabelList
                  dataKey={metricKey}
                  position="right"
                  formatter={(v) => (v >= 1000 ? `$${(v / 1000).toFixed(1)}k` : `$${Math.round(v)}`)}
                  style={{ fill: "#0F3B5C", fontSize: 11, fontWeight: 700 }}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Hotel weekly overview — moved from Dashboard */}
      <HotelWeeklyBoard />

      {/* Client table + turnover */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-base">Clients (12 months, ex-GST)</CardTitle>
            <Button data-testid="clients-table-expand" size="sm" variant="outline" className="h-8" onClick={() => setTableExpanded((e) => !e)}>
              {tableExpanded ? <><Minimize2 className="h-3.5 w-3.5 mr-1" /> Top 10</> : <><Maximize2 className="h-3.5 w-3.5 mr-1" /> All</>}
            </Button>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="analytics-clients-table">
              <thead>
                <tr className="text-xs uppercase text-muted-foreground border-b">
                  <Th label="Hotel" k="hotel_name" right={false} />
                  <Th label="Revenue" k="revenue_ex_gst" />
                  <Th label="$/week" k="revenue_per_week" />
                  <Th label="Orders" k="orders" />
                  <Th label="Pieces" k="items_dispatched" />
                  <Th label={<>Holding<RfidTag /></>} k="items_held" />
                  <Th label={<>Overdue<RfidTag /></>} k="overdue_now" />
                  <Th label="OD %" k="overdue_pct" />
                </tr>
              </thead>
              <tbody>
                {tableData.map((c) => (
                  <tr key={c.hotel_name} className="border-b last:border-0 hover:bg-slate-50">
                    <td className="py-1.5 pr-2 truncate max-w-[160px] font-medium">{c.hotel_name}</td>
                    <td className="py-1.5 pr-2 text-right font-mono">{fmt(c.revenue_ex_gst)}</td>
                    <td className="py-1.5 pr-2 text-right font-mono">{fmt(c.revenue_per_week)}</td>
                    <td className="py-1.5 pr-2 text-right font-mono">{c.orders}</td>
                    <td className="py-1.5 pr-2 text-right font-mono">{c.items_dispatched}</td>
                    <td className="py-1.5 pr-2 text-right font-mono">{c.items_held}</td>
                    <td className={`py-1.5 pr-2 text-right font-mono ${c.overdue_now > 0 ? "text-red-600 font-bold" : ""}`}>{c.overdue_now}</td>
                    <td className={`py-1.5 pr-2 text-right font-mono ${c.overdue_pct > 0 ? "text-red-600" : "text-slate-400"}`}>
                      {c.overdue_pct != null ? `${c.overdue_pct}%` : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-[11px] text-muted-foreground mt-2">
              Pieces = ordered quantities on dispatched orders (12 mo) · Holding / Overdue = live RFID status · OD % = Overdue ÷ Holding
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 gap-2">
            <CardTitle className="text-base">Inventory turnover</CardTitle>
            <div className="flex items-center gap-2">
              <Select value={turnoverMonth || ""} onValueChange={setTurnoverMonth}>
                <SelectTrigger className="h-8 w-[120px] text-xs" data-testid="turnover-month-select">
                  <SelectValue placeholder="Month" />
                </SelectTrigger>
                <SelectContent>
                  {[...months].reverse().map((m) => (
                    <SelectItem key={m.month} value={m.month}>{m.month}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button data-testid="turnover-expand" size="sm" variant="outline" className="h-8" onClick={() => setTurnoverExpanded((e) => !e)}>
                {turnoverExpanded ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {turnover && selTurnover && (
              <div className="grid grid-cols-2 gap-3" data-testid="analytics-turnover">
                <Mini label={`Dispatched (${selTurnover.month})`} value={selTurnover.dispatched_items} />
                <Mini label={<>Washed ({selTurnover.month})<RfidTag /></>} value={selTurnover.received_items} />
                <Mini
                  label={`Turnover (${selTurnover.month})`}
                  value={totalItems > 0 ? `${((selTurnover.dispatched_items / totalItems) * 100).toFixed(0)}%` : "—"}
                  hint={`${selTurnover.dispatched_items} pcs / ${totalItems} total`}
                />
                <Mini label={<>Written off ({selTurnover.month})<RfidTag /></>} value={selTurnover.deleted_items} red={selTurnover.deleted_items > 0} />
                <Mini label={<>Avg days at hotel (now)<RfidTag /></>} value={turnover.avg_days_at_hotel != null ? `${turnover.avg_days_at_hotel}d` : "—"} />
                <Mini label={<>Avg washes / item<RfidTag /></>} value={turnover.avg_washes_per_item ?? "—"} />
                <Mini label={<>Missing now<RfidTag /></>} value={turnover.missing_now} red={turnover.missing_now > 0} />
                <Mini label={<>At hotels / warehouse<RfidTag /></>} value={`${turnover.at_hotel} / ${turnover.at_warehouse}`} />
              </div>
            )}
            {turnoverExpanded && (
              <div className="mt-4 border-t pt-3 overflow-x-auto" data-testid="turnover-monthly-table">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left uppercase text-muted-foreground border-b">
                      <th className="py-1.5 pr-2">Month</th>
                      <th className="py-1.5 pr-2 text-right">Dispatched</th>
                      <th className="py-1.5 pr-2 text-right whitespace-nowrap">Washed<RfidTag /></th>
                      <th className="py-1.5 pr-2 text-right">Turnover</th>
                      <th className="py-1.5 pr-2 text-right whitespace-nowrap">Written off<RfidTag /></th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...months].reverse().map((m) => (
                      <tr key={m.month} className={`border-b last:border-0 ${m.month === turnoverMonth ? "bg-sky-50" : ""}`}>
                        <td className="py-1.5 pr-2 font-mono font-semibold">{m.month}</td>
                        <td className="py-1.5 pr-2 text-right font-mono">{m.dispatched_items || "—"}</td>
                        <td className="py-1.5 pr-2 text-right font-mono">{m.received_items || "—"}</td>
                        <td className="py-1.5 pr-2 text-right font-mono">
                          {totalItems > 0 && m.dispatched_items ? `${((m.dispatched_items / totalItems) * 100).toFixed(0)}%` : "—"}
                        </td>
                        <td className={`py-1.5 pr-2 text-right font-mono ${m.deleted_items ? "text-red-600" : ""}`}>{m.deleted_items || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {editMonth && (
        <CostsDialog month={editMonth} onClose={() => setEditMonth(null)} onSaved={() => { setEditMonth(null); load(); }} />
      )}
    </div>
  );
}

const RfidTag = () => (
  <span className="ml-1 inline-block align-middle text-[9px] font-bold text-sky-700 bg-sky-50 border border-sky-200 rounded px-1 leading-4 normal-case tracking-normal" title="Counted from live RFID data">RFID</span>
);

function Kpi({ label, value, tone, icon: Icon, testid }) {
  const tones = { blue: "text-sky-700", amber: "text-amber-700", green: "text-emerald-700", red: "text-red-600", neutral: "text-brand-navy" };
  return (
    <Card data-testid={testid}>
      <CardContent className="p-5">
        <p className="text-[11px] uppercase tracking-[0.14em] font-semibold text-muted-foreground">{label}</p>
        <p className={`font-display text-3xl font-extrabold mt-1 flex items-center gap-2 ${tones[tone] || ""}`}>
          {Icon && <Icon className="h-6 w-6" />}{value}
        </p>
      </CardContent>
    </Card>
  );
}

function Mini({ label, value, hint, red }) {
  return (
    <div className="rounded-md border bg-neutral-50 px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">{label}</div>
      <div className={`font-mono font-extrabold text-xl ${red ? "text-red-600" : ""}`}>{value}</div>
      {hint && <div className="text-[10px] text-muted-foreground">{hint}</div>}
    </div>
  );
}

function CostsDialog({ month, onClose, onSaved }) {
  const [form, setForm] = useState(() => ({
    labor: month.costs?.labor || 0,
    utilities: month.costs?.utilities || 0,
    supplies: month.costs?.supplies || 0,
    rent: month.costs?.rent || 0,
    vehicle: month.costs?.vehicle || 0,
    maintenance: month.costs?.maintenance || 0,
    other: month.costs?.other || [],
    revenue_adjustment: month.revenue_adjustment || 0,
    notes: month.notes || "",
  }));
  const [saving, setSaving] = useState(false);
  const costTotal = COST_FIELDS.reduce((s, [k]) => s + (Number(form[k]) || 0), 0)
    + form.other.reduce((s, o) => s + (Number(o.amount) || 0), 0);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/analytics/costs/${month.month}`, {
        ...form,
        labor: Number(form.labor) || 0, utilities: Number(form.utilities) || 0,
        supplies: Number(form.supplies) || 0, rent: Number(form.rent) || 0,
        vehicle: Number(form.vehicle) || 0, maintenance: Number(form.maintenance) || 0,
        revenue_adjustment: Number(form.revenue_adjustment) || 0,
        other: form.other.filter((o) => o.label).map((o) => ({ label: o.label, amount: Number(o.amount) || 0 })),
      });
      toast.success(`Costs saved for ${month.month}`);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="analytics-costs-dialog">
        <DialogHeader><DialogTitle>Costs — {month.month} <span className="text-xs font-normal text-amber-700">(all ex-GST)</span></DialogTitle></DialogHeader>
        <div className="space-y-3">
          {COST_FIELDS.map(([k, label]) => (
            <div key={k} className="flex items-center justify-between gap-3">
              <label className="text-sm">{label}</label>
              <Input data-testid={`analytics-cost-${k}`} type="number" className="w-32 h-9 text-right" value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
            </div>
          ))}
          <div className="border-t pt-2 space-y-2">
            {form.other.map((o, i) => (
              <div key={i} className="flex items-center gap-2">
                <Input placeholder="Label" className="h-9" value={o.label} onChange={(e) => { const n = [...form.other]; n[i] = { ...o, label: e.target.value }; setForm({ ...form, other: n }); }} />
                <Input type="number" placeholder="$" className="w-28 h-9 text-right" value={o.amount} onChange={(e) => { const n = [...form.other]; n[i] = { ...o, amount: e.target.value }; setForm({ ...form, other: n }); }} />
                <Button size="sm" variant="ghost" onClick={() => setForm({ ...form, other: form.other.filter((_, j) => j !== i) })}><Trash2 className="h-4 w-4 text-red-500" /></Button>
              </div>
            ))}
            <Button data-testid="analytics-add-other" size="sm" variant="outline" onClick={() => setForm({ ...form, other: [...form.other, { label: "", amount: "" }] })}>
              <Plus className="h-4 w-4 mr-1" /> Other cost
            </Button>
          </div>
          <div className="flex items-center justify-between gap-3 border-t pt-3">
            <label className="text-sm">Revenue adjustment (± ex-GST)<br /><span className="text-xs text-muted-foreground">Auto revenue: {fmt2(month.revenue_auto)}</span></label>
            <Input data-testid="analytics-rev-adj" type="number" className="w-32 h-9 text-right" value={form.revenue_adjustment} onChange={(e) => setForm({ ...form, revenue_adjustment: e.target.value })} />
          </div>
          <Input placeholder="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          <div className="flex items-center justify-between pt-2">
            <span className="text-sm font-bold">Total costs: {fmt2(costTotal)}</span>
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button data-testid="analytics-costs-save" onClick={save} disabled={saving}>{saving ? "Saving…" : "Save"}</Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
